import { useState } from "react";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";

export default function VipPage() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", marketing_consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    if (!form.marketing_consent) { setError("Please tick the marketing consent box to join and receive your 10% coupon."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { ...form, source_page: "vip" });
      setDoneMsg(data?.message || "");
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-24" data-testid="vip-page">
      <SectionTitle eyebrow="Free to Join" title="Join Our VIP List" subtitle="Sign up today and receive 10% OFF your first order. Be the first to hear about exclusive offers, new arrivals and when the next fireworks season begins." />

      <div className="mt-8 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-6 text-center" data-testid="vip-promo-banner">
        <p className="font-playfair text-xl sm:text-2xl text-white leading-snug">
          Join our mailing list and get a unique <span className="cf-gold-metallic font-bold">10% coupon</span> for your first order
        </p>
        <p className="mt-2 text-sm sm:text-base font-semibold cf-gold-text tracking-wide">Valid until 31 December 2026</p>
      </div>

      <GlassCard className="mt-6 p-8" hover={false}>
        {done ? (
          <div className="text-center py-8" data-testid="vip-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <p className="mt-4 font-playfair text-2xl text-white">You're on the list!</p>
            <p className="mt-2 text-muted-foreground">{doneMsg || "We'll be in touch with early access and exclusive offers."}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="vip-form">
            <div><Label className="text-white/80">Name *</Label><Input data-testid="vip-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div><Label className="text-white/80">Email *</Label><Input data-testid="vip-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div><Label className="text-white/80">Mobile (optional)</Label><Input data-testid="vip-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <label className="flex items-start gap-2 text-sm text-white/70"><input type="checkbox" data-testid="vip-consent" className="mt-1" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} required /> I would like to receive seasonal offers, promotions and updates from Clacton Fireworks. I understand that I can unsubscribe at any time. <span className="text-firered">*</span></label>
            {error && <p className="text-sm text-firered" data-testid="vip-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="vip-submit" className="cf-gold-btn font-semibold w-full gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get 10% OFF
            </Button>
          </form>
        )}
      </GlassCard>
    </div>
  );
}
